import type { UserProfile } from "../types";
import type { ConnectionDot } from "../utils/connectionState";
import { GearIcon } from "./icons";
import styles from "./UserProfileBar.module.css";

interface UserProfileBarProps {
  readonly profile: UserProfile | null;
  readonly loading: boolean;
  readonly personaName?: string | null;
  /**
   * When provided, overrides the MSAL-derived displayName. Used to prefer
   * the Studio settings endpoint's `user.displayName` (curated by an admin)
   * over the raw Azure AD claim ("Boopathiraja..." truncation, surname-first
   * locale variants, etc.).
   */
  readonly displayName?: string | null;
  /**
   * Dot colour (ST-02): online teal, degraded amber, offline red, unknown grey.
   * Defaults to "online" so standalone usages keep the connected appearance.
   */
  readonly connectionStatus?: ConnectionDot;
  /**
   * Tooltip and aria-label, supplied by the caller so it can name the failing
   * service. Falls back to a generic per-state label when omitted.
   */
  readonly connectionTooltip?: string;
  readonly onSettingsClick?: () => void;
}

/** Only `online` pulses — a pulse on a settled outage would misread as "in progress". */
const DOT_STATUS: Record<ConnectionDot, { cls: string | undefined; title: string }> = {
  online: { cls: "", title: "Connected" },
  degraded: { cls: styles.degraded, title: "Partially connected" },
  offline: { cls: styles.offline, title: "Disconnected" },
  unknown: { cls: styles.unknown, title: "Status unknown" },
};

function computeInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function UserProfileBar({
  profile,
  loading,
  personaName,
  displayName,
  connectionStatus = "online",
  connectionTooltip,
  onSettingsClick,
}: Readonly<UserProfileBarProps>) {
  if (!profile || loading) return null;

  const resolvedName = displayName?.trim() || profile.displayName;
  const resolvedInitials =
    displayName?.trim() ? computeInitials(displayName) : profile.initials;
  // AIVA-5213: show only the persona returned by the Studio /settings
  // response. Do not fall back to the Azure AD job title or a hardcoded
  // label — those are not the user's AiVA persona and were being mistaken
  // for one. No persona → no badge.
  const badge = personaName?.trim() || null;
  const dot = DOT_STATUS[connectionStatus];
  const dotTitle = connectionTooltip ?? dot.title;

  return (
    <div className={styles.profileBar}>
      <div className={styles.avatar}>{resolvedInitials}</div>
      <div className={styles.profileInfo}>
        <span className={styles.profileName}>{resolvedName}</span>
        {badge && <span className={styles.profileBadge}>{badge}</span>}
      </div>
      <output
        className={`${styles.onlineDot} ${dot.cls ?? ""}`.trim()}
        title={dotTitle}
        aria-label={dotTitle}
      />
      <button
        type="button"
        className={styles.gearBtn}
        title="Settings"
        aria-label="Settings"
        onClick={onSettingsClick}
      >
        <GearIcon />
      </button>
    </div>
  );
}
